import { createAsyncThunk } from '@reduxjs/toolkit'
import callAPI, { ResponseType } from 'api'
import store, { Response } from 'store'
import { Success, Fail } from 'store/types'
import { ERROR_CODES } from 'api/response'
import { navigate } from 'utils/history'
import Toast from 'components/Toast'
import { setSuccess, setFetchFail } from 'api/remoteData'
import { ROUTES, ROUTE_BASE } from '../constants/constants'

import {
  GetLiveCallResponse,
  CreateLiveCallPayload,
  CreateLiveCallResponse,
  MakeLiveCallPayload,
  MakeLiveCallResponse,
  UpdateLiveCallPayload,
  UpdateLiveCallLogPayload,
  UpdateLiveCallLogResponse,
  GetLiveCallStatusResponse,
  RecoverLiveCallRecordingPayload,
  RecoverLiveCallRecordingResponse,
} from './types/live-call'

export const getLiveCall = async (payload: string) =>
  await callAPI<GetLiveCallResponse, { live_call_id: string }>('crm_get_live_call', [{ live_call_id: payload }], {
    requireAuthorization: true,
  })

export const createLiveCall = createAsyncThunk<Fail | Success<CreateLiveCallResponse>, CreateLiveCallPayload>(
  'liveCall/createLiveCall',
  async (payload) =>
    await callAPI<CreateLiveCallResponse, CreateLiveCallPayload>('crm_create_live_call', [payload], {
      requireAuthorization: true,
      reloadOnReauth: false,
    }),
)

export const updateLiveCall = async (payload: UpdateLiveCallPayload) =>
  await callAPI<{ success: boolean }, UpdateLiveCallPayload>('crm_update_live_call', [payload], {
    requireAuthorization: true,
    reloadOnReauth: false,
  })

export const updateLiveCallLog = createAsyncThunk<Fail | Success<UpdateLiveCallLogResponse>, UpdateLiveCallLogPayload>(
  'liveCall/updateLiveCallLog',
  async ({ onFail: _onFail, onSuccess: _onSuccess, ...payload }: UpdateLiveCallLogPayload) =>
    await callAPI('crm_update_live_call', [payload], { requireAuthorization: true, reloadOnReauth: false }),
)

export const makeLiveCallApi = async (
  payload: MakeLiveCallPayload,
  dialerUrl: string = process.env.DIALER_SERVER || '/dialer',
) =>
  await fetch(`${dialerUrl}/make_call`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${store.getState().auth.token}`,
    },
    body: JSON.stringify(payload),
  })
    .then((res: Response) =>
      res
        .json()
        .then((json: ResponseType<any>) => {
          if (!res.ok) {
            return setFetchFail({
              code: res.status,
              message: json.error?.message || '',
              name: res.statusText,
            })
          }
          if (json.error) {
            store.dispatch(Response.setError(json.error))
            if (json.error.code === ERROR_CODES.TOKEN_IS_EXPIRED) {
              Toast.toastMessage({ message: 'token_expired', variant: 'error' })
              navigate(`/${ROUTE_BASE.AUTH}/${ROUTES.LOGIN}`)
            }

            return setFetchFail(json.error)
          }
          store.dispatch(Response.reset())
          return setSuccess(json.result)
        })
        .catch((err: Error) =>
          setFetchFail({
            message: err.message,
            name: 'INVALID_JSON',
            code: -2,
          }),
        ),
    )
    .catch((err: Error) =>
      setFetchFail({
        name: err.name,
        message: err.message === 'Failed to fetch' ? 'Check your network connection.' : err.message,
        code: -1,
      }),
    )

export const makeLiveCall = createAsyncThunk<Fail | Success<MakeLiveCallResponse>, MakeLiveCallPayload>(
  'liveCall/makeLiveCall',
  async (payload: MakeLiveCallPayload) => await makeLiveCallApi(payload),
)

export const getLiveCallStatus = createAsyncThunk<Fail | Success<GetLiveCallStatusResponse>>(
  'liveCall/getLiveCallStatus',
  async () =>
    await callAPI<GetLiveCallStatusResponse>('crm_get_live_call_active', undefined, { requireAuthorization: true }),
)

export const recoverLiveCallRecording = async (liveCallId: string) =>
  await callAPI<RecoverLiveCallRecordingResponse, RecoverLiveCallRecordingPayload>(
    'crm_recover_live_call_recording',
    [{ live_call_id: liveCallId }],
    { requireAuthorization: true, reloadOnReauth: false },
  )
